import { supabase } from "../providers/supabase/supabase";
import type { Modulo } from "../organizacoes/useOrganizacaoAtiva";

/**
 * Link de contratação por parceira (migration 287). O corretor preenche o
 * link público; a North libera em Acesso Restrito › Contratações; a parceira
 * acompanha as indicações dela.
 */

export type TipoContratacao = "individual" | "escritorio";
export type StatusPedido = "aguardando" | "liberado" | "recusado";

/** Versão do texto de aceite gravada no pedido. Mudou o texto, muda a versão. */
export const ACEITE_VERSAO = "2026-10-29";

export interface InfoLink {
  parceira: string;
  logo_url: string | null;
  cor_primaria: string | null;
}

export interface EnvioContratacao {
  codigo: string;
  tipo: TipoContratacao;
  nome: string;
  cpfCnpj: string;
  email: string;
  celular: string;
  escritorioNome?: string;
  escritorioCnpj?: string;
  usuariosPrevistos?: number | null;
}

export interface LinkContratacao {
  id: string;
  codigo: string;
  parceira_id: string;
  gerente_id: number | null;
  modulos: Modulo[];
  ativo: boolean;
  criado_em: string;
}

export interface PedidoContratacao {
  id: string;
  link_id: string;
  tipo: TipoContratacao;
  nome: string;
  cpf_cnpj: string;
  email: string;
  celular: string;
  escritorio_nome: string | null;
  escritorio_cnpj: string | null;
  usuarios_previstos: number | null;
  status: StatusPedido;
  motivo: string | null;
  criado_em: string;
  decidido_em: string | null;
}

export interface Indicacao {
  id: string;
  tipo: TipoContratacao;
  nome: string;
  escritorio: string | null;
  status: StatusPedido;
  criado_em: string;
  decidido_em: string | null;
  usuarios: number;
}

type Erro = { message: string; code?: string };
const rpc = (nome: string, args?: Record<string, unknown>) =>
  supabase.rpc(nome as never, args as never) as unknown as Promise<{ data: unknown; error: Erro | null }>;

/** Mensagem do banco quando é de validação (P0001); senão a genérica. */
const mensagem = (error: Erro, padrao: string) => (error.code === "P0001" ? error.message : padrao);

export const urlDoLink = (codigo: string) => `${window.location.origin}/#/contratar/${codigo}`;

export async function infoDoLink(codigo: string): Promise<InfoLink | null> {
  const { data, error } = await rpc("contratacao_link_info", { p_codigo: codigo });
  if (error) throw new Error("Não foi possível abrir o link.");
  const linha = Array.isArray(data) ? data[0] : data;
  return (linha as InfoLink) ?? null;
}

export async function enviarContratacao(e: EnvioContratacao): Promise<void> {
  const escritorio = e.tipo === "escritorio";
  const { error } = await rpc("contratacao_enviar", {
    p_codigo: e.codigo,
    p_tipo: e.tipo,
    p_nome: e.nome,
    p_cpf_cnpj: e.cpfCnpj,
    p_email: e.email,
    p_celular: e.celular,
    p_escritorio_nome: escritorio ? e.escritorioNome ?? null : null,
    p_escritorio_cnpj: escritorio ? e.escritorioCnpj || null : null,
    p_usuarios_previstos: escritorio ? e.usuariosPrevistos ?? null : null,
    p_aceite_versao: ACEITE_VERSAO,
  });
  if (error) throw new Error(mensagem(error, "Não foi possível enviar o cadastro."));
}

export async function listarLinks(): Promise<LinkContratacao[]> {
  const { data, error } = await supabase
    .from("contratacao_links" as never)
    .select("id, codigo, parceira_id, gerente_id, modulos, ativo, criado_em")
    .order("criado_em", { ascending: false });
  if (error) throw new Error("Não foi possível carregar os links.");
  return (data ?? []) as LinkContratacao[];
}

export async function listarPedidos(): Promise<PedidoContratacao[]> {
  const { data, error } = await supabase
    .from("contratacao_pedidos" as never)
    .select(
      "id, link_id, tipo, nome, cpf_cnpj, email, celular, escritorio_nome, escritorio_cnpj, usuarios_previstos, status, motivo, criado_em, decidido_em",
    )
    .order("criado_em", { ascending: false })
    .limit(500);
  if (error) throw new Error("Não foi possível carregar os pedidos.");
  return (data ?? []) as PedidoContratacao[];
}

export async function listarIndicacoes(): Promise<Indicacao[]> {
  const { data, error } = await rpc("contratacao_minhas_indicacoes");
  if (error) throw new Error("Não foi possível carregar as indicações.");
  return (data ?? []) as Indicacao[];
}

export async function salvarLink(l: {
  id: string | null;
  parceiraId: string;
  codigo: string;
  modulos: Modulo[];
  gerenteId: number | null;
  ativo: boolean;
}): Promise<string> {
  const { data, error } = await rpc("contratacao_salvar_link", {
    p_id: l.id,
    p_parceira_id: l.parceiraId,
    p_codigo: l.codigo,
    p_modulos: l.modulos,
    p_gerente_id: l.gerenteId,
    p_ativo: l.ativo,
  });
  if (error) throw new Error(mensagem(error, "Não foi possível salvar o link."));
  return String(data);
}

/**
 * Libera o pedido: prepara a organização (o banco cria o escritório na 1ª
 * vez), convida pelo invite-user nessa organização e fecha o pedido. Se o
 * convite falhar, o pedido continua aguardando e pode ser liberado de novo.
 */
export async function liberarPedido(pedidoId: string): Promise<void> {
  const { data, error } = await rpc("contratacao_preparar", { p_pedido: pedidoId });
  if (error) throw new Error(mensagem(error, "Não foi possível preparar a liberação."));
  const destino = (Array.isArray(data) ? data[0] : data) as {
    organizacao_id: string;
    role_id: string;
    nome: string;
    email: string;
    cpf_cnpj: string;
  } | null;
  if (!destino) throw new Error("Não foi possível preparar a liberação.");

  const convite = await supabase.functions.invoke("invite-user", {
    body: {
      email: destino.email,
      name: destino.nome,
      cpfCnpj: destino.cpf_cnpj,
      roleId: destino.role_id,
      isSuperAdmin: false,
      organizacaoId: destino.organizacao_id,
    },
  });
  if (convite.error) {
    let detalhe = "";
    try {
      detalhe = (await (convite.error as { context?: Response }).context?.json())?.error ?? "";
    } catch {
      // corpo sem JSON: fica a mensagem genérica
    }
    throw new Error(detalhe || "Não foi possível enviar o convite.");
  }

  const fim = await rpc("contratacao_concluir", { p_pedido: pedidoId });
  if (fim.error) throw new Error(mensagem(fim.error, "Convite enviado, mas não foi possível concluir o pedido."));
}

export async function recusarPedido(pedidoId: string, motivo: string): Promise<void> {
  const { error } = await rpc("contratacao_recusar", { p_pedido: pedidoId, p_motivo: motivo });
  if (error) throw new Error(mensagem(error, "Não foi possível recusar o pedido."));
}

/** Só dígitos; CPF 11, CNPJ 14. */
export const soDigitos = (v: string) => v.replace(/\D/g, "");

export const formatarDocumento = (d: string | null) => {
  const n = soDigitos(d ?? "");
  if (n.length === 11) return n.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
  if (n.length === 14) return n.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
  return n;
};

export const formatarCelular = (c: string | null) => {
  const n = soDigitos(c ?? "");
  if (n.length === 11) return `(${n.slice(0, 2)}) ${n.slice(2, 7)}-${n.slice(7)}`;
  if (n.length === 10) return `(${n.slice(0, 2)}) ${n.slice(2, 6)}-${n.slice(6)}`;
  return n;
};

export const ROTULO_STATUS: Record<StatusPedido, string> = {
  aguardando: "Aguardando liberação",
  liberado: "Liberado",
  recusado: "Recusado",
};

export const ROTULO_TIPO: Record<TipoContratacao, string> = {
  individual: "Individual",
  escritorio: "Escritório",
};
